/**
 * Status of the public/shareable link for a page.
 * Mirrors the GraphQL PublicLinkPageStatus enum as a runtime value.
 */
export enum PublicLinkPageStatus {
	BLOCKED_BY_ORG = 'BLOCKED_BY_ORG',
	BLOCKED_BY_ORG_DEFAULT_POLICY = 'BLOCKED_BY_ORG_DEFAULT_POLICY',
	BLOCKED_BY_PRODUCT = 'BLOCKED_BY_PRODUCT',
	BLOCKED_BY_SPACE = 'BLOCKED_BY_SPACE',
	BLOCKED_BY_CONTAINER_POLICY = 'BLOCKED_BY_CONTAINER_POLICY',
	BLOCKED_BY_CLASSIFICATION_LEVEL = 'BLOCKED_BY_CLASSIFICATION_LEVEL',
	OFF = 'OFF',
	ON = 'ON',
	SITE_BLOCKED = 'SITE_BLOCKED',
	SITE_DISABLED = 'SITE_DISABLED',
	SPACE_BLOCKED = 'SPACE_BLOCKED',
	SPACE_DISABLED = 'SPACE_DISABLED',
	TRASHED = 'TRASHED',
}

/**
 * The access scope of an access link.
 * - `INTERNAL_LICENSED`: Workspace link — accessible to licensed users only
 * - `EXTERNAL`: Public link — accessible to anyone with the URL
 *
 * Note: named ConfluenceShareableLinkAccessType to match the GraphQL schema enum.
 * Use AccessLinkAccessType as the preferred alias in consuming code.
 */
export type ConfluenceShareableLinkAccessType = 'INTERNAL_LICENSED' | 'EXTERNAL';
/** Preferred alias for ConfluenceShareableLinkAccessType */
export type AccessLinkAccessType = ConfluenceShareableLinkAccessType;

export type RefetchType = 'all' | 'permission' | 'public-link';

/**
 * Computed access link state for a page, combining data from the page query,
 * content query, and permissions query.
 */
export type AccessLinkPageDataCalculated = {
	/** Are access links enabled by admins at site and space levels */
	isAccessLinkAdminEnabled: boolean;
	/** Is access link allowed for content type, admin enabled, and status ON */
	isAccessLinkFullyEnabled: boolean;
	/** Are access links allowed for this content type */
	isAccessLinkAllowedForContentType: boolean;
	/** Are editable access links allowed for this content type */
	isAccessLinkEditAllowedForContentType: boolean;
	/** Does the access link itself have edit permissions (e.g. editable public whiteboards) */
	isEditAccessLink: boolean;
	/** Can current user edit access link status for this page */
	canEditAccessLink: boolean;
	accessLinkStatus?: PublicLinkPageStatus | null;
	accessLinkSharePath?: string | null;
	/** The access scope of the access link (INTERNAL_LICENSED, EXTERNAL, or null if no link) */
	accessType?: AccessLinkAccessType | null;
	refetch: (dataToFetch?: RefetchType) => void;
};

/**
 * Common data always present in the access link page provider response,
 * regardless of loading state.
 */
interface AccessLinkPageProviderCommonData {
	errors: Error[];
}

/**
 * Discriminated union returned by useAccessLinkPageDataProvider.
 * When `isLoading` is true, only `errors` is available.
 * When `isLoading` is false, the full computed state is available.
 */
export type AccessLinkPageProviderData = AccessLinkPageProviderCommonData &
	(
		| { isLoading: true }
		| ({ isLoading: false } & AccessLinkPageDataCalculated)
	);

